/**
 * The arithmetic every zoomable surface needs, independent of how the zoom is
 * finally applied. The image viewer scales a CSS transform; the Mermaid dialog
 * grows a layout box so its scroll container gets real scrollbars. Those two
 * cannot share the application step, but a wheel gesture means the same thing
 * over either one, and that is what lives here.
 *
 * Pure, so bounds and gesture arithmetic can be tested without a browser.
 */

/** One mouse-wheel detent in pixels: the `deltaY` Chrome reports per notch. */
export const WHEEL_PIXELS_PER_STEP = 100;
/** A `deltaMode: DOM_DELTA_LINE` wheel event, in pixels per line. */
const PIXELS_PER_LINE = 16;
/** A `deltaMode: DOM_DELTA_PAGE` wheel event, in pixels per page. */
const PIXELS_PER_PAGE = 400;

/** The range a surface allows, as multiples of its fit-to-view size. */
export interface ZoomBounds {
  min: number;
  max: number;
}

export function clampZoom(zoom: number, { min, max }: ZoomBounds): number {
  return Math.min(max, Math.max(min, zoom));
}

/**
 * Wheel events arrive in whatever unit the device reports: pixels from a
 * trackpad, lines from some mice, pages from a few. Normalize to pixels so a
 * zoom step means the same thing everywhere.
 */
function normalizeWheelDelta(deltaY: number, deltaMode: number): number {
  if (deltaMode === 1) return deltaY * PIXELS_PER_LINE;
  if (deltaMode === 2) return deltaY * PIXELS_PER_PAGE;
  return deltaY;
}

/**
 * How many geometric zoom steps a wheel gesture asks for. Positive zooms in,
 * matching the convention that scrolling up moves toward the content.
 *
 * One mouse detent is exactly one step, so the wheel and a `+` button agree.
 * The result is deliberately fractional: a trackpad emits a stream of
 * few-pixel deltas, and rounding each to a whole step would either quantize
 * them away or launch the surface to its bound on the first flick.
 */
export function wheelZoomSteps(deltaY: number, deltaMode: number): number {
  return -normalizeWheelDelta(deltaY, deltaMode) / WHEEL_PIXELS_PER_STEP;
}

/**
 * `zoom` moved `steps` geometric steps of size `factor`, clamped to `bounds`.
 * Geometric so the steps feel even at every scale, and fractional-step safe so
 * the same call serves a button (`steps: 1`) and a trackpad (`steps: 0.03`).
 */
export function zoomBySteps(
  zoom: number,
  steps: number,
  factor: number,
  bounds: ZoomBounds,
): number {
  return clampZoom(zoom * factor ** steps, bounds);
}
